import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import useFetch from 'use-http';
import Button from 'src/components/controls/forms/redesign/FormButton';
import Panel from 'src/components/common/Panel';
import { ODataOptions, ODataFilter, ODataFilterOperators, ODataOrderByDirection } from 'src/util/ODataBuilder';
import { ITableConfig } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import Vendor from 'src/models/AP/Vendor';

export interface IAp1099SearchCriteria {
    vendorNumber?: string;
    taxYear?: string;
}

interface IAp1099SearchProps {
    onSearch: (criteria: IAp1099SearchCriteria) => void;
}

interface IOptionType {
    value: string;
    label: string;
}

// Self-contained Vendor No. lookahead (its own debounced fetch against AccountsPayable/vendors,
// same pattern as VendorSearch.tsx) plus a Tax Year filter - the two fields that actually key the
// LM_1099 grid. Deliberately does not reuse VendorSearch.tsx directly since this dialog is meant to
// be opened from more than one entry point later and shouldn't depend on a host page's own vendor
// list/state.
const Ap1099Search = (props: IAp1099SearchProps) => {
    const { control, getValues, reset, register } = useForm<IAp1099SearchCriteria>();
    const { get, response } = useFetch();
    const [vendorOptions, setVendorOptions] = useState<Array<IOptionType>>([]);
    const [isSearchingVendors, setIsSearchingVendors] = useState<boolean>(false);

    // Loads the first page (unfiltered) on mount so the dropdown already has vendors to pick from
    // before the user types anything, matching VendorsPage.tsx's own vendor search - it always has
    // an initial page loaded via its own onCreate, rather than starting empty until a keystroke.
    useEffect(() => {
        fetchVendors('');
    }, []);

    const fetchVendors = async (term: string) => {
        const tableConfig: ITableConfig = { rowCount: 25, idField: 'VNDR_NO', pageNumber: 1, columns: [] };
        const options = ODataOptions.from(tableConfig);
        // GetVendors' SQL always appends "OFFSET ... FETCH NEXT ... ROWS ONLY" unconditionally, which
        // is invalid T-SQL without a preceding ORDER BY - every other caller of this endpoint always
        // sets one (see VendorsPage.tsx's initializeVendorSearchSettings), so this needs to as well.
        options.addOrderBy('VendorNumber', ODataOrderByDirection.Ascending);

        // Mirrors VendorsPage.tsx's own getVendors exactly: a numeric term filters by vendor number
        // OR name (in case the digits happen to appear in a name too), a non-numeric term filters by
        // name only. Field names are raw SQL with the table alias GetVendors' own query uses
        // (vd.NAME1/vd.VNDR_NO), not the mapped Vendor.vendorName/vendorNumber property names -
        // GetWhereClauseFromFilter concatenates filter.Field into the SQL as-is.
        if (!!term && term.length > 0) {
            if (isNaN(Number(term))) {
                options.addFilter(new ODataFilter('vd.NAME1', ODataFilterOperators.Contains, term));
            } else {
                options.addFilter(new ODataFilter('vd.VNDR_NO', ODataFilterOperators.Equals, term));
                options.addFilter(new ODataFilter('vd.NAME1', ODataFilterOperators.Contains, term));
            }
        }

        const result: PagedResultSet<Vendor> = await get(`/v1.0/AccountsPayable/vendors${options.toString()}`);

        if (response.ok && Array.isArray(result?.items)) {
            setVendorOptions(result.items.map((v) => ({
                value: `${v.vendorNumber}`,
                label: `${v.vendorName} (${v.vendorNumber})`
            })));
        }
    };

    const vendorSearch = _.debounce((inputText: string) => {
        fetchVendors(inputText);
    }, 500);

    const handleSearch = () => {
        const data = getValues();
        props.onSearch({ vendorNumber: data.vendorNumber, taxYear: data.taxYear });
    };

    const handleClear = () => {
        reset();
        setVendorOptions([]);
        props.onSearch({});
    };

    return (
        <Panel backgroundColor="#D3D3D3">
            <Panel panelTitle="Search">
                <div style={{ padding: '0 .5rem', marginTop: '-1rem' }}>
                    <div className="form-row">
                        <div style={{ width: '45%', marginRight: '.5rem' }}>
                            <label style={{ whiteSpace: 'nowrap' }}>Vendor</label>
                            <Controller
                                name="vendorNumber"
                                control={control}
                                defaultValue={null}
                                render={({ onChange, value }) => (
                                    <Select
                                        placeholder="Select Vendor"
                                        options={vendorOptions}
                                        value={value ? (vendorOptions.find((o) => o.value === value) ?? null) : null}
                                        onMenuOpen={() => setIsSearchingVendors(true)}
                                        onMenuClose={() => setIsSearchingVendors(false)}
                                        onBlur={() => setIsSearchingVendors(false)}
                                        menuIsOpen={isSearchingVendors}
                                        isSearchable={true}
                                        isClearable={true}
                                        // Without a portal, the menu renders inline in this
                                        // component's own DOM subtree with no z-index of its own -
                                        // the "1099 Records" grid panel that follows this search bar
                                        // in the dialog then paints on top of it (later DOM siblings
                                        // win ties in stacking order). Portaling to body and forcing
                                        // a high z-index sidesteps local stacking entirely.
                                        menuPortalTarget={document.body}
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        // react-select also fires onInputChange (with an empty string)
                                        // when it clears its own text after a selection, a menu close,
                                        // or a blur - re-fetching on those would replace vendorOptions
                                        // with the unfiltered top-25 list, which often doesn't include
                                        // the vendor just picked, making the selected value's lookup
                                        // below fail and the dropdown appear to clear itself. Only
                                        // actual typing (action 'input-change') should trigger a fetch.
                                        onInputChange={(text, { action }) => {
                                            if (action === 'input-change') {
                                                vendorSearch(text);
                                            }
                                        }}
                                        onChange={(opt: IOptionType | null) => onChange(opt?.value ?? null)}
                                    />
                                )}
                            />
                        </div>
                        <div style={{ width: '20%', marginRight: '.5rem' }}>
                            <label style={{ whiteSpace: 'nowrap' }}>Tax Year</label>
                            <input
                                type="number"
                                name="taxYear"
                                ref={register}
                                style={{ width: '100%', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem' }}
                            />
                        </div>
                        <div style={{ display: 'flex', marginLeft: 'auto', alignItems: 'center' }}>
                            <Button
                                text={'Search'}
                                className="blue-hover-button"
                                style={{ width: '7rem', height: '3rem', verticalAlign: 'middle', marginLeft: '1rem', marginTop: '1.7rem', borderRadius: '5px' }}
                                onClick={() => handleSearch()}
                            />
                            <Button
                                text="Clear"
                                className="blue-hover-button"
                                style={{ whiteSpace: 'nowrap', marginLeft: '1rem', marginTop: '1.7rem', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                onClick={() => handleClear()}
                            />
                        </div>
                    </div>
                </div>
            </Panel>
        </Panel>
    );
};

export default Ap1099Search;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/components/Ap1099Search.tsx